import React, { useMemo } from 'react';
import { Minus, Plus, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  formatPackagingSummary,
  lbsToUnit,
  selectionsTotalLbs,
  type PackagingOption,
  type PackagingSelection,
} from '../../utils/packagingMath';

type Props = {
  options: PackagingOption[];
  unit: 'LB' | 'KG';
  selections: PackagingSelection[];
  onChange: (next: PackagingSelection[]) => void;
  maxTotalLbs?: number | null;
  compact?: boolean;
};

export default function ProductPackagingPicker({
  options,
  unit,
  selections,
  onChange,
  maxTotalLbs,
  compact = false,
}: Props) {
  const { t } = useTranslation();
  const totalLbs = useMemo(
    () => selectionsTotalLbs(selections, options),
    [selections, options],
  );
  const totalInUnit = lbsToUnit(totalLbs, unit);
  const unitLabel = unit === 'KG' ? 'kg' : 'lb';
  const summary = formatPackagingSummary(selections, options);

  const setCount = (optionId: string, delta: number) => {
    const current = selections.find((s) => s.optionId === optionId)?.count ?? 0;
    const nextCount = Math.max(0, current + delta);
    const others = selections.filter((s) => s.optionId !== optionId);
    const next =
      nextCount > 0
        ? [...others, { optionId, count: nextCount }]
        : others;
    const nextLbs = selectionsTotalLbs(next, options);
    if (maxTotalLbs != null && maxTotalLbs > 0 && nextLbs > maxTotalLbs + 1e-6) {
      return;
    }
    onChange(next);
  };

  if (options.length === 0) return null;

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3'}>
      {!compact ? (
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
          <Package className="h-3.5 w-3.5" aria-hidden />
          {t('storefront.packaging_choose', { defaultValue: 'Choose packaging' })}
        </div>
      ) : null}
      <p className="text-xs text-slate-600">
        {options.length === 1
          ? t('storefront.packaging_single_hint', {
              defaultValue: 'Sold in {{size}} {{unit}} packages — order in multiples.',
              size: lbsToUnit(options[0].sizeLbs, unit),
              unit: unitLabel,
            })
          : t('storefront.packaging_multi_hint', {
              defaultValue: 'Mix package sizes to reach your total weight.',
            })}
      </p>
      <div className="space-y-2">
        {options.map((opt) => {
          const count = selections.find((s) => s.optionId === opt.id)?.count ?? 0;
          const sizeInUnit = lbsToUnit(opt.sizeLbs, unit);
          return (
            <div
              key={opt.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900">{opt.label}</div>
                <div className="text-xs text-slate-500">
                  {sizeInUnit} {unitLabel} each
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Decrease"
                  onClick={() => setCount(opt.id, -1)}
                  disabled={count <= 0}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-700 disabled:opacity-40"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[1.5rem] text-center text-sm font-semibold">{count}</span>
                <button
                  type="button"
                  aria-label="Increase"
                  onClick={() => setCount(opt.id, 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-[#00b853] bg-[#00b853] text-white"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
        <span className="font-semibold">
          {t('storefront.packaging_total', { defaultValue: 'Total' })}: {totalInUnit} {unitLabel}
        </span>
        {summary ? (
          <span className="mt-0.5 block text-xs text-emerald-800/90">{summary}</span>
        ) : (
          <span className="mt-0.5 block text-xs text-emerald-800/80">
            {t('storefront.packaging_add_one', { defaultValue: 'Add at least one package' })}
          </span>
        )}
      </div>
    </div>
  );
}
